import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Loader2,
  Trash2,
  Mail,
  Clock,
  Search,
  Dumbbell,
  CheckCircle2,
  RotateCcw,
} from "lucide-react";
import {
  adminApi,
  getToken,
  setToken,
  clearToken,
} from "@/lib/adminApi";

const inputCls =
  "w-full bg-[#121212] border border-white/15 text-white placeholder:text-neutral-500 px-4 py-3.5 focus:outline-none focus:border-[#E63946] transition-colors";

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await adminApi.login(email, password);
      setToken(data.token);
      toast.success(`Welcome back, ${data.name}`);
      onSuccess();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Login failed. Check your details."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-[#171717] border border-white/10 p-8 sm:p-10"
      >
        <div className="flex items-center gap-2 mb-8">
          <Dumbbell className="text-[#E63946]" size={22} />
          <span className="font-heading text-xl font-black uppercase tracking-tight">
            Admin<span className="text-[#E63946]">.</span>Panel
          </span>
        </div>
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">
          Coach Login
        </h1>
        <p className="text-neutral-400 text-sm mt-2 mb-7">
          View and manage your coaching applications.
        </p>

        <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
          Email
        </label>
        <input
          type="email"
          data-testid="admin-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className={`${inputCls} mb-5`}
        />

        <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
          Password
        </label>
        <input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className={`${inputCls} mb-7`}
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#D90429] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wide text-base px-8 py-4 transition-all"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Log In"}
        </button>
      </form>
    </div>
  );
}

function DashboardView({ onLogout }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.listApplications();
      setApps(data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Failed to load applications.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = async (a) => {
    const next = a.status === "contacted" ? "new" : "contacted";
    try {
      const updated = await adminApi.updateStatus(a.id, next);
      setApps((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete application from ${a.full_name}?`)) return;
    try {
      await adminApi.remove(a.id);
      setApps((prev) => prev.filter((x) => x.id !== a.id));
      toast.success("Application deleted.");
    } catch {
      toast.error("Could not delete application.");
    }
  };

  const filtered = apps.filter((a) => {
    const matchesQuery =
      a.full_name.toLowerCase().includes(query.toLowerCase()) ||
      a.email.toLowerCase().includes(query.toLowerCase());
    const matchesFilter = filter === "all" || a.status === filter;
    return matchesQuery && matchesFilter;
  });

  const newCount = apps.filter((a) => a.status !== "contacted").length;

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-black/70 backdrop-blur-xl z-10">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Dumbbell className="text-[#E63946]" size={20} />
            <span className="font-heading text-lg font-black uppercase tracking-tight">
              Applications
            </span>
            <span
              data-testid="new-count-badge"
              className="ml-2 text-xs bg-[#E63946] px-2 py-0.5 font-bold"
            >
              {newCount} NEW
            </span>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-neutral-400 hover:text-white transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500"
            />
            <input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email..."
              className={`${inputCls} pl-11`}
            />
          </div>
          <div className="flex gap-2">
            {["all", "new", "contacted"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-5 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${
                  filter === f
                    ? "bg-[#E63946] border-[#E63946] text-white"
                    : "border-white/15 text-neutral-400 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="animate-spin text-[#E63946]" size={32} />
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="empty-state"
            className="text-center py-24 text-neutral-500"
          >
            <Mail size={40} className="mx-auto mb-4 opacity-40" />
            No applications {filter !== "all" ? `marked "${filter}"` : "yet"}.
          </div>
        ) : (
          <div className="space-y-4" data-testid="applications-list">
            {filtered.map((a) => (
              <div
                key={a.id}
                data-testid={`application-row-${a.id}`}
                className={`bg-[#171717] border p-6 transition-colors ${
                  a.status === "contacted"
                    ? "border-white/10 opacity-70"
                    : "border-white/15"
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-xl font-bold uppercase tracking-tight">
                        {a.full_name}
                      </h3>
                      <span
                        className={`text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold ${
                          a.status === "contacted"
                            ? "bg-white/10 text-neutral-300"
                            : "bg-[#E63946] text-white"
                        }`}
                      >
                        {a.status === "contacted" ? "Contacted" : "New"}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-neutral-400 flex-wrap">
                      <a
                        href={`mailto:${a.email}`}
                        className="inline-flex items-center gap-1.5 hover:text-[#E63946]"
                      >
                        <Mail size={14} /> {a.email}
                      </a>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={14} /> {fmtDate(a.created_at)}
                      </span>
                    </div>
                    <div className="mt-3 text-xs uppercase tracking-widest text-[#E63946]">
                      {a.experience}
                    </div>
                    <p className="mt-3 text-neutral-200 leading-relaxed text-sm bg-[#121212] border border-white/10 p-4">
                      {a.goals}
                    </p>
                  </div>

                  <div className="flex md:flex-col gap-2 shrink-0">
                    <button
                      onClick={() => toggleStatus(a)}
                      data-testid={`toggle-status-${a.id}`}
                      className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white text-white text-xs uppercase tracking-widest px-4 py-2.5 transition-colors"
                    >
                      {a.status === "contacted" ? (
                        <>
                          <RotateCcw size={14} /> Mark New
                        </>
                      ) : (
                        <>
                          <CheckCircle2 size={14} /> Mark Contacted
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => remove(a)}
                      data-testid={`delete-${a.id}`}
                      className="inline-flex items-center justify-center gap-2 border border-[#E63946]/40 text-[#E63946] hover:bg-[#E63946] hover:text-white text-xs uppercase tracking-widest px-4 py-2.5 transition-colors"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    adminApi
      .me()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E63946]" size={32} />
      </div>
    );
  }

  return authed ? (
    <DashboardView onLogout={logout} />
  ) : (
    <LoginView onSuccess={() => setAuthed(true)} />
  );
}
